import { useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ExpandMore, RestartAlt, Save } from '@mui/icons-material'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import type { PopularityKnobDto } from '@api/types/popularity'
import {
  apiErrorMessage,
  usePopularityOptions,
  useResetPopularityOptions,
  useUpdatePopularityOptions,
} from '../hooks/useAdminJobs'
import { changedValues, groupKnobs, knobLabel, sameValue, validateKnob } from '../popularTimes'

/**
 * Every Popularity.* knob, grouped, with the server's help text, bounds and default. Saves only what changed
 * (partial PUT). Saving recomputes nothing: the hourly table follows within one job interval, typical weeks and
 * scores at the nightly pass, or run a recompute.
 */
export function PopularitySettingsEditor({ onDirtyChange }: {
  /** Told whenever the editor gains or loses unsaved edits, so the page can warn before a tab switch drops them. */
  onDirtyChange?: (dirty: boolean) => void
} = {}) {
  const options = usePopularityOptions()
  const update = useUpdatePopularityOptions()
  const reset = useResetPopularityOptions()
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)

  const knobs = useMemo(() => options.data?.knobs ?? [], [options.data])
  const groups = useMemo(() => groupKnobs(knobs), [knobs])
  const valueOf = (k: PopularityKnobDto) => drafts[k.name] ?? k.current
  const errors = useMemo(() => {
    const out: Record<string, string> = {}
    for (const k of knobs) {
      const d = drafts[k.name]
      if (d === undefined) continue
      const e = validateKnob(k, d)
      if (e) out[k.name] = e
    }
    return out
  }, [knobs, drafts])
  const changes = changedValues(knobs, drafts)
  const changeCount = Object.keys(changes).length
  const hasErrors = Object.keys(errors).length > 0
  const dirty = changeCount > 0 || hasErrors

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  // Unmounting (a tab switch the page allowed, leaving the page) means the drafts are gone.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  // A reload or closed tab would drop the drafts silently too.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const save = async () => {
    setServerError(null)
    setSaved(null)
    try {
      await update.mutateAsync({ values: changes })
      setDrafts({})
      setSaved(`Saved ${changeCount} setting${changeCount === 1 ? '' : 's'}. Recompute to apply them to existing data now.`)
    } catch (e) {
      setServerError(apiErrorMessage(e, 'The settings could not be saved.'))
    }
  }

  const doReset = async () => {
    setServerError(null)
    setSaved(null)
    try {
      await reset.mutateAsync()
      setDrafts({})
      setSaved('Every setting is back to its default.')
      setConfirmReset(false)
    } catch (e) {
      setServerError(apiErrorMessage(e, 'The settings could not be reset.'))
      setConfirmReset(false)
    }
  }

  if (options.isLoading) return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
  if (options.error) return <Alert severity="error">Could not load the popular-times settings: {apiErrorMessage(options.error)}</Alert>

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        {changeCount > 0 ? (
          <Chip
            color="warning"
            label={`${changeCount} unsaved change${changeCount === 1 ? '' : 's'}`}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Saved values are live within a minute.
          </Typography>
        )}
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button size="small" startIcon={<RestartAlt />} onClick={() => setConfirmReset(true)} disabled={reset.isPending}>
            Reset all
          </Button>
          <Button size="small" onClick={() => { setDrafts({}); setServerError(null) }} disabled={changeCount === 0 && !hasErrors}>
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={save}
            disabled={changeCount === 0 || hasErrors || update.isPending}
          >
            Save
          </Button>
        </Stack>
      </Stack>
      {serverError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError(null)}>{serverError}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(null)}>{saved}</Alert>}

      {groups.map((g, i) => (
        <Accordion key={g.key} defaultExpanded={i === 0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600 }}>{g.title}</Typography>
              <Typography variant="caption" color="text.secondary">{g.help}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {g.knobs.map((k) => {
                const value = valueOf(k)
                const modified = !sameValue(k, k.current, k.default)
                const dirty = drafts[k.name] !== undefined && !sameValue(k, drafts[k.name], k.current)
                return (
                  <Box key={k.name}>
                    <TextField
                      fullWidth
                      size="small"
                      label={knobLabel(k.name)}
                      type={k.dataType === 'string' ? 'text' : 'number'}
                      value={value}
                      onChange={(e) => setDrafts((d) => ({ ...d, [k.name]: e.target.value }))}
                      error={!!errors[k.name]}
                      helperText={errors[k.name] ?? k.description}
                      inputProps={k.dataType === 'string' ? undefined : { min: k.min, max: k.max, step: k.dataType === 'integer' ? 1 : 0.05 }}
                    />
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Default {k.default}{k.dataType !== 'string' ? ` · range ${k.min}–${k.max}` : ''}
                      </Typography>
                      {dirty && <Chip size="small" color="primary" variant="outlined" label="Unsaved" />}
                      {modified && !dirty && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={k.overriddenByDisplayName ? `Changed by ${k.overriddenByDisplayName}` : 'Changed'}
                        />
                      )}
                      {modified && (
                        <Button size="small" sx={{ minWidth: 0, p: 0 }} onClick={() => setDrafts((d) => ({ ...d, [k.name]: k.default }))}>
                          Use default
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <ConfirmDialog
        open={confirmReset}
        title="Reset every popular-times setting?"
        message="All overrides are removed and every knob returns to its default."
        confirmLabel="Reset all"
        destructive
        loading={reset.isPending}
        onConfirm={doReset}
        onClose={() => setConfirmReset(false)}
      />
    </Box>
  )
}
